/**
 * Compress an image *File/Blob* to a maximum width, returning a JPEG data URL.
 *
 * Prefer this over compressImage() for anything coming straight from an
 * <input type="file"> / photo picker. It decodes the picked file directly via
 * an object URL and never builds a full-resolution base64 copy in memory.
 *
 * Why this matters on iOS: the old path (FileReader.readAsDataURL → compressImage)
 * held the entire full-res photo as a giant base64 string AND decoded it, which
 * blew the WKWebView renderer's memory budget on 12MP iPhone HEIC photos — the
 * WebContent process got jetsam-killed and the screen went blank. Decoding from
 * the object URL and re-encoding to JPEG keeps peak memory to a single decode
 * and normalizes HEIC → JPEG. The <img> path also applies EXIF orientation, so
 * photos aren't rotated.
 *
 * @param {File|Blob} file - The picked image file
 * @param {number} maxWidth - Maximum pixel width (default 800)
 * @param {number} quality - JPEG quality 0-1 (default 0.8)
 * @returns {Promise<string>} Compressed JPEG data URL
 */
export function compressImageFile(file, maxWidth = 800, quality = 0.8) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let width = img.naturalWidth;
        let height = img.naturalHeight;

        // Only scale down, never up
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);

        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

/**
 * Compress an image *data URL* to a maximum width, returning a JPEG base64 string.
 *
 * Prefer compressImageFile() when you have a File — it avoids holding a full-res
 * base64 copy in memory. Use this only when the source is already a data URL.
 *
 * @param {string} dataUrl - The original image as a data URL
 * @param {number} maxWidth - Maximum pixel width (default 800)
 * @param {number} quality - JPEG quality 0-1 (default 0.8)
 * @returns {Promise<string>} Compressed data URL
 */
export function compressImage(dataUrl, maxWidth = 800, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      // Only scale down, never up
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const compressed = canvas.toDataURL('image/jpeg', quality);
      resolve(compressed);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}
